import React, { useEffect, useState } from 'react';
import { Box, Typography, Menu, Button } from '@mui/material';
import { Product } from '@domain/product/models/Product';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { DurationRow, ShippingMethodSelector, LeadTimeSummary } from '@features/products/components/leadtime';


interface LeadTimeDropdownProps {
  product?: Product;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (values: {
    productionDuration?: number;
    processingDuration?: number;
    defaultShipmentMethodId?: string;
  }) => void;
  isLoading?: boolean;
}

const LeadTimeDropdown: React.FC<LeadTimeDropdownProps> = ({
  product,
  anchorEl,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {

  const [productionDuration, setProductionDuration] = useState(product?.productionDuration);
  const [processingDuration, setProcessingDuration] = useState(product?.processingDuration);
  const [selectedShipmentMethod, setSelectedShipmentMethod] = useState(product?.defaultShipmentMethod);

  // Sync from server only when opening the menu or switching products — not when `product`
  // refetches after editing a shipment method (same id), or we'd overwrite the updated method.
  useEffect(() => {
    if (!open || !product) return;
    setProductionDuration(product.productionDuration);
    setProcessingDuration(product.processingDuration);
    setSelectedShipmentMethod(product.defaultShipmentMethod);
  }, [open, product?.id]);


  const handleShipmentMethodChange = async (method: ShipmentMethod) => {
    setSelectedShipmentMethod(method);
  };

  const handleShipmentMethodCreate = (method: ShipmentMethod) => {
    setSelectedShipmentMethod(method);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 0, width: 400 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', px: 3, pt: 2 }}>
            Lead Time
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DurationRow
              label="Production Time"
              value={productionDuration}
              onChange={(value) => setProductionDuration(value)}
            />

            <ShippingMethodSelector
              selectedMethod={selectedShipmentMethod}
              onMethodChange={handleShipmentMethodChange}
              onMethodCreate={handleShipmentMethodCreate}
            />

            <DurationRow
              label="Processing Time"
              value={processingDuration}
              onChange={(value) => setProcessingDuration(value)}
            />

            <LeadTimeSummary
              productionDuration={productionDuration}
              shippingDuration={selectedShipmentMethod?.duration}
              processingDuration={processingDuration}
            />


            <Box sx={{ p: 3, pt: 0 }}>
              <Button
                variant="contained"
                onClick={() =>
                  onConfirm({
                    productionDuration,
                    processingDuration,
                    defaultShipmentMethodId: selectedShipmentMethod?.id,
                  })
                }
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? 'Saving...' : 'Confirm Lead Time'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Menu >

    </>
  );
};

export default LeadTimeDropdown;
